import jwtDecode from "jwt-decode";
import mixpanel from "mixpanel-browser";

const TOKEN_KEY = "jwtToken";
const USER_KEY = "userData";
const LOGOUT_SIGNAL_KEY = "bpi:logout";
const EXPIRY_CHECK_INTERVAL_MS = 60 * 1000;

// Keys that hold personal data and must not outlive the session that created them.
const PERSONAL_DATA_KEYS = [
  TOKEN_KEY,
  USER_KEY,
  "newAccountConnectedEmail",
  "lastPlatformCreated",
];
const PERSONAL_DATA_PREFIXES = ["inboxData_", "inboxFetchError_"];

// The session token and profile are kept in sessionStorage so the browser
// discards them when the tab or window is closed.
const sessionStore = () => window.sessionStorage;

export const getToken = (): string | null => sessionStore().getItem(TOKEN_KEY);

export const getStoredUser = <T = any>(): T | null => {
  const raw = sessionStore().getItem(USER_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as T;
  } catch {
    return null;
  }
};

export const setSession = (token: string, user: unknown) => {
  sessionStore().setItem(TOKEN_KEY, token);
  sessionStore().setItem(USER_KEY, JSON.stringify(user));
};

export const setStoredUser = (user: unknown) => {
  sessionStore().setItem(USER_KEY, JSON.stringify(user));
};

export const decodeToken = <T = Record<string, any>>(
  token: string | null = getToken(),
): T | null => {
  if (!token) return null;
  try {
    return jwtDecode<T>(token);
  } catch {
    return null;
  }
};

// Tokens without an exp claim are left to the API to reject.
export const isTokenExpired = (token: string | null = getToken()): boolean => {
  if (!token) return true;
  const decoded = decodeToken<{ exp?: number }>(token);
  if (!decoded) return true;
  return typeof decoded.exp === "number" && decoded.exp * 1000 <= Date.now();
};

export const hasActiveSession = (): boolean => {
  const token = getToken();
  return !!token && !isTokenExpired(token);
};

const expireAllCookies = () => {
  const labels = window.location.hostname.split(".");
  const domains = [""];
  for (let i = 0; i < labels.length - 1; i++) {
    domains.push(labels.slice(i).join("."));
  }
  document.cookie.split(";").forEach((cookie) => {
    const name = cookie.split("=")[0].trim();
    if (!name) return;
    domains.forEach((domain) => {
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/${
        domain ? `; domain=${domain}` : ""
      }`;
    });
  });
};

/**
 * Removes everything this origin keeps in the browser for the signed-in user:
 * localStorage, sessionStorage, readable cookies (on this host and parent
 * domains) and the analytics identity. Other open tabs are told to do the same.
 */
export const clearClientStorage = () => {
  try {
    mixpanel.reset();
  } catch {
    // mixpanel not initialised
  }
  window.localStorage.clear();
  window.sessionStorage.clear();
  expireAllCookies();
  window.localStorage.setItem(LOGOUT_SIGNAL_KEY, String(Date.now()));
  window.localStorage.removeItem(LOGOUT_SIGNAL_KEY);
};

export const endClientSession = (redirectTo = "/login") => {
  clearClientStorage();
  window.location.assign(redirectTo);
};

/**
 * Drops personal data left in localStorage by a session that is no longer
 * active (browser closed without logging out, token expired, or data written
 * by builds that kept the session in localStorage).
 */
export const purgeOrphanedPersonalData = () => {
  const legacyToken = window.localStorage.getItem(TOKEN_KEY);
  const legacyUser = window.localStorage.getItem(USER_KEY);
  if (!getToken() && legacyToken && legacyUser && !isTokenExpired(legacyToken)) {
    sessionStore().setItem(TOKEN_KEY, legacyToken);
    sessionStore().setItem(USER_KEY, legacyUser);
  }
  if (!hasActiveSession()) {
    sessionStore().removeItem(TOKEN_KEY);
    sessionStore().removeItem(USER_KEY);
  }
  const storage = window.localStorage;
  for (let i = storage.length - 1; i >= 0; i--) {
    const key = storage.key(i);
    if (!key) continue;
    const isLegacySessionKey = key === TOKEN_KEY || key === USER_KEY;
    const isPersonal =
      PERSONAL_DATA_KEYS.includes(key) ||
      PERSONAL_DATA_PREFIXES.some((prefix) => key.startsWith(prefix));
    if (isLegacySessionKey || (isPersonal && !hasActiveSession())) {
      storage.removeItem(key);
    }
  }
};

/**
 * Ends this tab's session when the token expires or when the user logs out in
 * another tab.
 */
export const watchSession = () => {
  window.addEventListener("storage", (event) => {
    if (
      event.storageArea === window.localStorage &&
      event.key === LOGOUT_SIGNAL_KEY &&
      event.newValue &&
      getToken()
    ) {
      endClientSession();
    }
  });
  window.setInterval(() => {
    const token = getToken();
    if (token && isTokenExpired(token)) {
      endClientSession();
    }
  }, EXPIRY_CHECK_INTERVAL_MS);
};
